<template>
  <div class="container">
    <div class="title">
      <div>{{ t('about-timeline-title-1') }}</div>
      <div>{{ t('about-timeline-title-2') }} <span>{{ t('about-timeline-title-3') }}</span></div>
    </div>
  </div>

  <!-- Desktop timeline view -->
  <div class="timeline desktop-only">
    <div class="container">
      <TimelineItem 
        v-for="(item, index) in timelineItems" 
        :key="index"
        :date="item.date"
        :logo="item.logo"
        :description="item.description"
        :isEven="index % 2 === 0"
      />
    </div>
  </div>

  <!-- Mobile view - stacked items -->
  <div class="mobile-timeline mobile-only">
    <div class="container">
      <div class="mobile-item" v-for="(item, index) in timelineItems" :key="index">
        <div class="date"><span>{{ item.date }}</span></div>
        <div class="logo" v-if="item.logo">
          <img v-if="typeof item.logo === 'string'" :src="item.logo">
          <template v-else>
            <img v-for="(logo, logoIndex) in item.logo" :key="logoIndex" :src="logo">
          </template>
        </div>
        <div class="description">
          {{ item.description }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TimelineItem from './TimelineItem.vue';

const { t } = useI18n();

const timelineItems = ref([
  {
    date: t('about-timeline-item-1-date'),
    logo: null,
    description: t('about-timeline-item-1-description')
  },
  {
    date: t('about-timeline-item-2-date'),
    logo: null,
    description: t('about-timeline-item-2-description')
  },
  {
    date: t('about-timeline-item-3-date'),
    logo: '/static/logo/logo-spf.png',
    description: t('about-timeline-item-3-description')
  },
  {
    date: t('about-timeline-item-4-date'),
    logo: ['/static/logo/logo-eurocc.png', '/static/logo/logo-stp.png'],
    description: t('about-timeline-item-4-description')
  },
  {
    date: t('about-timeline-item-5-date'),
    logo: null,
    description: t('about-timeline-item-5-description')
  },
  {
    date: t('about-timeline-item-6-date'),
    logo: '/static/logo/logo-sub.png',
    description: t('about-timeline-item-6-description')
  },
  {
    date: t('about-timeline-item-7-date'),
    logo: '/static/excellence_in_innovation.png',
    description: t('about-timeline-item-7-description')
  }
]);
</script>

<style lang="scss" scoped>
.title {
  margin-top: 50px;
  font-size: 38px;
  line-height: 1;
  font-family: $font-regular;
  color: $color-purple;
  span {
    color: $color-pink-dark-2
  }
}

/* Desktop/Mobile visibility */
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: block;
  }
}

/* Timeline styles */
.timeline {
  color: $color-purple;
  padding: 30px 0;
  position: relative;
  font-size: 14px;

  &::after {
    content: '';
    position: absolute;
    width: 1px;
    background-color: #ccc;
    top: 0;
    bottom: 0;
    left: 50%;
    margin-left: -1px;
  }

  .container {
    position: relative;
  }
}

/* Mobile timeline styles */
.mobile-timeline {
  color: $color-purple;
  padding: 20px 0;
  font-size: 14px;

  .mobile-item {
    border-bottom: 1px solid #eee;
    padding: 20px 0;
    margin-bottom: 10px;

    &:last-child {
      border-bottom: none;
    }
  }

  .date {
    margin-bottom: 10px;
    text-align: center;

    span {
      background: $color-pink-dark-2;
      color: #fff;
      padding: 7px;
      font-size: 12px;
      border-radius: 20px;
    }
  }

  .logo {
    margin: 15px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;

    img {
      max-height: 100px;
      width: auto;
      margin: 5px;
    }
  }

  .description {
    margin-top: 10px;
    text-align: center;
  }
}

/* Start Up, Bulgaria! (logo-sub.png) — slightly smaller than other timeline logos */
.timeline :deep(.item:nth-child(6) .logo img) {
  max-height: 100px;
}

.mobile-timeline .mobile-item:nth-child(6) .logo img {
  max-height: 85px;
}
</style>
